import type { CreateUserBody } from '@movo/contracts/api/users';
import { USER_EMAIL_PATTERN, USER_PASSWORD_MIN_LENGTH } from '@movo/contracts/domain/user';

/** I filtri come li tengono i campi: la stringa vuota vale "tutti". */
export type UserFilters = { search: string; role: string; status: string };

export type UsersListParams = {
  page: number;
  limit: number;
  search: string;
  role: string;
  active: '' | 'true' | 'false';
};

export const EMPTY_FILTERS: UserFilters = { search: '', role: '', status: '' };

const ACTIVE_BY_STATUS: Record<string, 'true' | 'false'> = { active: 'true', inactive: 'false' };

/** I parametri di `GET /api/users`: il backend filtra lo stato con `active=true|false`. */
export function toListParams(
  { search, role, status }: UserFilters,
  page: number,
  limit: number
): UsersListParams {
  return { page, limit, search, role, active: ACTIVE_BY_STATUS[status] ?? '' };
}

export const sameFilters = (a: UserFilters, b: UserFilters) =>
  a.search === b.search && a.role === b.role && a.status === b.status;

export const totalPagesOf = (total: number, limit: number) => Math.max(1, Math.ceil(total / limit));

export const usersSubtitle = (total: number) => (total === 1 ? '1 utente' : `${total} utenti`);

const PASSWORD_TOO_SHORT = `La password deve avere almeno ${USER_PASSWORD_MIN_LENGTH} caratteri`;

export type NewUserForm = Omit<CreateUserBody, 'personalInfo'>;

export const NEW_USER_FORM: NewUserForm = { username: '', email: '', password: '', role: 'user' };

export type NewUserErrors = Partial<Record<'username' | 'email' | 'password', string>>;

/** Gli errori per campo del nuovo utente, con le regole del backend; vuoto se va bene. */
export function newUserErrors({ username, email, password }: NewUserForm): NewUserErrors {
  const errors: NewUserErrors = {};
  if (!username.trim()) errors.username = 'Lo username è obbligatorio';
  if (!USER_EMAIL_PATTERN.test(email)) errors.email = 'Formato email non valido';
  if (password.length < USER_PASSWORD_MIN_LENGTH) errors.password = PASSWORD_TOO_SHORT;
  return errors;
}

export type PasswordErrors = { password?: string; confirmation?: string };

/** Gli errori per campo della nuova password; vuoto se va bene. */
export function passwordErrors(password: string, confirmation: string): PasswordErrors {
  if (password.length < USER_PASSWORD_MIN_LENGTH) return { password: PASSWORD_TOO_SHORT };
  if (password !== confirmation) return { confirmation: 'Le password non coincidono' };
  return {};
}
